import axios, { AxiosError } from "axios";
import { UseQueryResult, useQuery } from "react-query";
import {
  StrapiProductAreaResponse,
  StrapiProductSectionResponse,
  StrapiRequest,
} from "../model/HelpCentreService";
import { StrapiComingSoonFeatureResponse } from "../../../coming-soon/services/models/ComingSoonService";
import { StrapiLoginResponse } from "../../../auth/services/model/LoginService";

export type StrapiOperators =
  | "$eq"
  | "$eqi"
  | "$ne"
  | "$nei"
  | "$lt"
  | "$lte"
  | "$gt"
  | "$gte"
  | "$in"
  | "$notIn"
  | "$contains"
  | "$notContains"
  | "$containsi"
  | "$notContainsi"
  | "$null"
  | "$notNull"
  | "$between"
  | "$startsWith"
  | "$startsWithi"
  | "$endsWith"
  | "$endsWithi";

export type StrapiFilterValue = {
  [K in StrapiOperators]?: string | number | boolean | (string | number)[];
};

export type StrapiFilter = {
  [key: string]: StrapiFilterValue;
};

export type StrapiCMSPromiseType =
  | StrapiProductAreaResponse
  | StrapiProductSectionResponse
  | StrapiComingSoonFeatureResponse
  | StrapiLoginResponse;

export const StrapiCMSApiService = async (
  path: string,
  id?: number,
  populate?: string,
  filters?: StrapiFilter
): Promise<StrapiCMSPromiseType> => {
  let uri = `${process.env.REACT_APP_STRAPI_API_URL}/api/${path}`;

  if (id) {
    uri += `/${id}`;
  }

  const queryParts = [];

  if (populate) {
    if (populate.startsWith("=")) {
      queryParts.push(`populate=${populate.substring(1)}`);
    } else {
      queryParts.push(`populate${populate}`);
    }
  }

  if (filters) {
    Object.entries(filters).forEach(([field, conditions]) => {
      Object.entries(conditions).forEach(([operator, value]) => {
        queryParts.push(`filters[${field}][${operator}]=${value}`);
      });
    });
  }

  if (queryParts.length > 0) {
    uri += `?${queryParts.join("&")}`;
  }

  const { data } = await axios.get(uri, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${process.env.REACT_APP_STRAPI_API_TOKEN}`,
    },
  });
  return data;
};

const useHelpCentreProductArea = ({
  id,
  populate,
  filters,
}: StrapiRequest & { filters?: StrapiFilter }): UseQueryResult<
  StrapiProductAreaResponse,
  AxiosError
> =>
  useQuery<
    Promise<StrapiCMSPromiseType>,
    AxiosError,
    StrapiProductAreaResponse
  >(["HelpCentreProductArea", id, filters], () =>
    StrapiCMSApiService("help-centre-product-areas", id, populate, filters)
  );

const useHelpCentreProductSection = ({
  id,
  populate,
  filters,
}: StrapiRequest & { filters?: StrapiFilter }): UseQueryResult<
  StrapiProductSectionResponse,
  AxiosError
> =>
  useQuery<
    Promise<StrapiCMSPromiseType>,
    AxiosError,
    StrapiProductSectionResponse
  >(["HelpCentreProductSection", id, filters], () =>
    StrapiCMSApiService("help-centre-product-sections", id, populate, filters)
  );

export { useHelpCentreProductArea, useHelpCentreProductSection };
